import api from './api';
import {DateTime} from 'luxon';
import {
  PlayerMetricsReport,
  PlayerSessionsReportSession,
  Team,
  TeamSessionsReportRow,
} from '../interfaces';
import apiGraphQL from './apiGraphQL';

export function getTeamPlayerSessionsReport({
  teamId,
  startDate,
  endDate,
  profileIds,
  sessionTypes,
}: {
  teamId: number;
  startDate: Date;
  endDate: Date;
  profileIds?: number[];
  sessionTypes?: string[];
}) {
  return new Promise<PlayerSessionsReportSession[]>((resolve, reject) => {
    try {
      let url = `/util/reports?teamId=${teamId}&reportType=playerSessions`;
      if (startDate && endDate) {
        url += `&startDate=${DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd')}&endDate=${DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd')}`;
      }
      if (profileIds && profileIds.length > 0) {
        url += `&profileIds=${profileIds.join(',')}`;
      }
      if (sessionTypes && sessionTypes.length > 0) {
        url += `&sessionTypes=${sessionTypes.join(',')}`;
      }
      api
        .get(url)
        .then(response => {
          resolve(response.data as PlayerSessionsReportSession[]);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getTeamPlayerMetricsReport({
  teamId,
  startDate,
  endDate,
  profileIds,
  sessionTypes,
}: {
  teamId: number;
  startDate: Date;
  endDate: Date;
  profileIds?: number[];
  sessionTypes?: string[];
}) {
  return new Promise<PlayerMetricsReport>((resolve, reject) => {
    try {
      let url = `/util/reports?teamId=${teamId}&reportType=playerMetrics`;
      if (startDate && endDate) {
        url += `&startDate=${DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd')}&endDate=${DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd')}`;
      }
      if (profileIds && profileIds.length > 0) {
        url += `&profileIds=${profileIds.join(',')}`;
      }
      if (sessionTypes && sessionTypes.length > 0) {
        url += `&sessionTypes=${sessionTypes.join(',')}`;
      }
      api
        .get(url)
        .then(response => {
          resolve(response.data as PlayerMetricsReport);
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getTeamSessionsReport({
  teamId,
  startDate,
  endDate,
  sessionTypes,
}: {
  teamId: number;
  startDate: Date;
  endDate: Date;
  sessionTypes?: string[];
}) {
  return new Promise<TeamSessionsReportRow[]>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query teamSessionsReport($teamId: Int!, $startDate: String!, $endDate: String!, $sessionTypes: [String]) {
            teamSessionsReport(teamId: $teamId, startDate: $startDate, endDate: $endDate, sessionTypes: $sessionTypes) {
              sessions {
                sessionId
                localDisplayStartTime
                startTime
                endTime
                duration
                sessionType
                activePlayerCount
                teamTotalLoad
                teamAvgLoad {
                  teamAvg
                  forwardAvg
                  defenseAvg
                }
                energyRatio {
                  teamAvg
                  forwardAvg
                  defenseAvg
                }
                practiceIntensity {
                  teamAvg
                  forwardAvg
                  defenseAvg
                }
                avgHustleScore {
                  teamAvg
                  forwardAvg
                  defenseAvg
                }
              }
            }
          }`,
          variables: {
            teamId,
            startDate: DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd'),
            endDate: DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd'),
            sessionTypes:
              sessionTypes && sessionTypes.length > 0 ? sessionTypes : null,
          },
        })
        .then(response => {
          resolve(
            response.data.data.teamSessionsReport
              .sessions as TeamSessionsReportRow[],
          );
        })
        .catch((e: Error) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
}

export function getTeam({
  id,
  acwrScoreStartDate,
  acwrScoreEndDate,
}: {
  id: number;
  acwrScoreStartDate?: string;
  acwrScoreEndDate?: string;
}) {
  return new Promise<Team>((resolve, reject) => {
    try {
      apiGraphQL
        .post('/graphql', {
          query: `query team($id: Int!, $acwrScoreStartDate: String, $acwrScoreEndDate: String) {
            team(id: $id, acwrScoreStartDate: $acwrScoreStartDate, acwrScoreEndDate: $acwrScoreEndDate) {
              id
              players {
                id
                profile {
                  id
                  firstName
                  lastName
                  position
                  acwrScores {
                    id
                    date
                    acwrScore
                    acuteWorkload
                    chronicWorkload
                  }
                }
              }
            }
          }`,
          variables: {
            id,
            acwrScoreStartDate,
            acwrScoreEndDate,
          },
        })
        .then(response => {
          resolve(response.data.data.team);
        });
    } catch (e) {
      reject(e);
    }
  });
}
